import { Grid, Box, styled, Typography } from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FormInputLable from "src/components/FormInputLabel";
import { GrayButton } from "src/components/GrayButton";
import { StyledTextField } from "src/components/Input";
import { StyledLoadingButton } from "src/components/LoadingButton";
import { StyledTextFieldMulti } from "src/components/MultilineInput";
import { AppDispatch, RootState } from "src/store";
import colors from "src/theme/colors";
import * as Yup from "yup";
import { contactValue, vendorValue } from "../redux/types";
import { postContact, postVendor, reset } from "../redux/VendorSlice";

interface Props {
  vendorId: string;
  onCancel: () => void;
}

export const ContactNewForm = (props: Props) => {
  const { vendorId, onCancel } = props;

  const navigate = useNavigate();

  //formik---------------------------------
  const VendorSchema = Yup.object().shape({
    name: Yup.string().required(),
    title: Yup.string().required(),
    phone: Yup.string(),
    mobile: Yup.string(),
    address: Yup.string(),
    email: Yup.string().email(),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      title: "",
      phone: "",
      mobile: "",
      email: "",
      address: "",
      vendorId: Number(vendorId),
    },
    validationSchema: VendorSchema,

    onSubmit: async (values: contactValue) => {
      dispatch(postContact(values));
    },
  });
  const { errors, touched, resetForm, handleSubmit, getFieldProps } = formik;

  //redux ---------------------------------
  const dispatch = useDispatch<AppDispatch>();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading, isError, isSuccess, message } = useSelector(
    (state: RootState) => state.vendor
  );

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(message, { variant: "error" });
    }
    if (isSuccess) {
      enqueueSnackbar(message, { variant: "success" });
      onCancel();
    }
    dispatch(reset());
  }, [isError, isSuccess, message, navigate, dispatch]);

  const onCancelForm = () => {
    resetForm();
    onCancel();
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container rowSpacing={12} columnSpacing={{ sm: 0, md: 20 }}>
            <Grid item xs={12} md={6}>
              <FormInputLable requried>Name</FormInputLable>
              <StyledTextField
                placeholder="Enter contact name"
                fullWidth
                {...getFieldProps("name")}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInputLable requried>Title</FormInputLable>
              <StyledTextField
                placeholder="Enter contact title"
                fullWidth
                {...getFieldProps("title")}
                error={Boolean(touched.title && errors.title)}
                helperText={touched.title && errors.title}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInputLable noPrefix>Phone</FormInputLable>
              <StyledTextField
                placeholder="Enter contact phone number"
                fullWidth
                {...getFieldProps("phone")}
                error={Boolean(touched.phone && errors.phone)}
                helperText={touched.phone && errors.phone}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInputLable noPrefix>Mobile</FormInputLable>
              <StyledTextField
                placeholder="Enter contact mobile number"
                fullWidth
                {...getFieldProps("mobile")}
                error={Boolean(touched.mobile && errors.mobile)}
                helperText={touched.mobile && errors.mobile}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInputLable noPrefix>Address</FormInputLable>
              <StyledTextFieldMulti
                placeholder="Enter contact address"
                fullWidth
                multiline
                rows={10}
                {...getFieldProps("address")}
                error={Boolean(touched.address && errors.address)}
                helperText={touched.address && errors.address}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInputLable noPrefix>Email</FormInputLable>
              <StyledTextField
                placeholder="Enter contact email address"
                fullWidth
                {...getFieldProps("email")}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
          </Grid>
        </Box>
        <Actions>
          <Box width="184px">
            <GrayButton
              variant="outlined"
              fullWidth
              disabled={isLoading}
              onClick={onCancelForm}
            >
              Cancel
            </GrayButton>
          </Box>
          <Box width="184px">
            <StyledLoadingButton
              loading={isLoading}
              variant="contained"
              color="primary"
              fullWidth
              type="submit"
            >
              <Typography
                fontSize={16}
                fontWeight={500}
                color={colors.gray[850]}
              >
                Add Contact
              </Typography>
            </StyledLoadingButton>
          </Box>
        </Actions>
      </Form>
    </FormikProvider>
  );
};

const Actions = styled(Box)(({ theme }) => ({
  marginTop: "40px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "24px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column-reverse",
    gap: "16px",
    "& > div": {
      width: "100%",
    },
  },
}));
